import React, { useEffect, useState } from "react";
import { Container } from "../common/Container";
import { AppTypography } from "../common/Typography";
import AppButton from "../common/Button";
import { useTheme } from "../../theme/ThemeContext";
import { Box, Paper, IconButton, Divider, Grid } from "@mui/material";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Close as CloseIcon,
  ShoppingCart as CartIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import styled from "@emotion/styled";
import { getCart, updateCartItem, removeCartItem } from "../../utils/cart";
import Toaster from "../utils/Toaster";
import { usePreserveStoreIdNavigate } from "../utils/usePreserveStoreIdNavigate";
import { useStoreInfo } from "../../context/StoreInfoContext";

const ProductImage = styled.img`
  width: 112px;
  height: 112px;
  object-fit: contain;
  border-radius: 10px;
  // border: 1px solid #e5e7eb;
  flex-shrink: 0;
  @media (max-width: 400px) {
    width: 80px;
    height: 80px;
  }
`;

const Cart = () => {
  const navigate = usePreserveStoreIdNavigate();
  const theme = useTheme();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPrice, setTotalPrice] = useState(0);
  const { storeInfo } = useStoreInfo();

  // Helper function to check if a size is onesize (case-insensitive)
  const isOnesize = (size) => {
    if (!size) return false;
    const sizeLower = size.toLowerCase().trim();
    return (
      sizeLower === "onesize" ||
      sizeLower === "one size" ||
      sizeLower === "one-size"
    );
  };
  const getMaxQuantity = (item) => {
    if (item?.limited_stock === false) return 99999;
    return (
      item?.max_quantity ??
      item?.available_quantity ??
      item?.quantity_available ??
      item?.quantity ??
      0
    );
  };

  // Load cart from sessionStorage
  useEffect(() => {
    setLoading(true);
    const cartData = getCart();
    setCart(cartData);
    setLoading(false);
  }, []);

  // Update total price when cart changes
  useEffect(() => {
    const total = cart?.reduce(
      (sum, item) => sum + item.selected_quantity * (item.selling_price || 0),
      0,
    );
    setTotalPrice(total);
  }, [cart]);

  const handleQuantityChange = (index, currentQuantity, increment) => {
    const item = cart?.[index];
    if (!item) return;

    const newQuantity = currentQuantity + increment;

    // Enforce minimum quantity of 1
    if (newQuantity < 1) return;

    // Enforce maximum quantity limit
    // Use max_quantity if available (stored when item was added), otherwise fallback to available/quantity if present
    const maxQuantity = getMaxQuantity(item);
    const finalQuantity = Math.min(newQuantity, maxQuantity);

    // Only update if quantity changed and is within limits
    if (finalQuantity !== currentQuantity) {
      updateCartItem(item.variant_id, finalQuantity, item.pack_id);
      const updatedCart = cart?.map((i, idx) =>
        idx === index ? { ...i, selected_quantity: finalQuantity } : i,
      );
      setCart(updatedCart);
    }
  };

  const handleRemoveItem = (index) => {
    const item = cart?.[index];
    if (!item) return;

    removeCartItem(item.variant_id, item.pack_id);
    const updated = cart?.filter((_, idx) => idx !== index);
    setCart(updated);
    if (updated.length === 0) {
      setTotalPrice(0);
    }
  };

  const handleCheckout = () => {
    navigate("/checkout");
  };

  const handleContinueShopping = () => {
    navigate("/");
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ py: 4, flex: 1 }}>{/* <CartSkeleton /> */}</Box>
      </Container>
    );
  }

  if (cart.length === 0) {
    return (
      <Container>
        <Box
          sx={{
            py: 8,
            textAlign: "center",
            ...theme?.background,
            minHeight: "60vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
          }}
        >
          <CartIcon
            sx={{
              fontSize: 80,
              ...theme?.typography?.body,
              opacity: 0.3,
              mb: 2,
            }}
          />
          <AppTypography
            variant="h4"
            style={theme?.typography?.heading}
            fontWeight={600}
            fontSize="2rem"
            sx={{ mb: 2 }}
          >
            Your cart is empty
          </AppTypography>
          <AppTypography
            variant="body1"
            style={theme?.typography?.body}
            sx={{ mb: 4, opacity: 0.7 }}
          >
            Looks like you haven't added any items to your cart yet.
          </AppTypography>
          <AppButton
            variant="contained"
            size="large"
            onClick={handleContinueShopping}
            sx={{
              backgroundColor: theme?.app_button_color,
              color: theme?.app_button_text_color || "#fff",
              "&:hover": {
                backgroundColor: theme?.app_button_color,
                opacity: 0.9,
              },
            }}
          >
            Start Shopping
          </AppButton>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Box sx={{ py: 4 }}>
        <AppTypography
          variant="h4"
          fontWeight={400}
          fontSize="26.25px"
          sx={{ mb: 3, color: theme?.text_color }}
        >
          Shopping Cart
        </AppTypography>

        <Grid container spacing={{ xs: 2, lg: 4 }}>
          {/* Cart Items - Card layout */}
          <Grid item xs={12} lg={8}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                borderRadius: 2,
              }}
            >
              {cart?.map((item, index) => (
                <Paper
                  key={`${item.pack_id ?? "nopack"}-${item.variant_id}-${index}`}
                  elevation={0}
                  sx={{
                    ...theme?.layout_styles?.paper,
                    backgroundColor: "#FFFFFF",
                    borderRadius: "14px",
                    p: { xs: 2, sm: 2.5 },
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                    alignItems: { xs: "stretch", sm: "flex-start" },
                    boxShadow:
                      "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px;",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flex: 1,
                      minWidth: 0,
                      alignItems: "stretch",
                    }}
                  >
                    <ProductImage src={item.image_url} alt={item.name} />
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: { xs: "80px", sm: "112px" },
                        justifyContent: "space-between",
                        pb: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Box>
                        <AppTypography
                          variant="body1"
                          fontWeight={400}
                          fontSize="14px"
                          sx={{ color: "#101828" }}
                        >
                          {item.name}
                        </AppTypography>
                        {item.description && (
                          <AppTypography
                            variant="body2"
                            sx={{
                              fontSize: "12.25px",
                              fontWeight: 400,
                              color: "#4A5565",
                              mt: 0.25,
                            }}
                          >
                            {item.description}
                          </AppTypography>
                        )}
                        {item.selected_size && (
                          <AppTypography
                            variant="body2"
                            sx={{
                              fontSize: "12.25px",
                              fontWeight: 400,
                              color: "#6A7282",
                              mt: 0.5,
                            }}
                          >
                            {item.selected_color
                              ? `Size: ${item.selected_size} | Color: ${item.selected_color}`
                              : isOnesize(item.selected_size)
                                ? "Size: One size"
                                : `Size: ${item.selected_size}`}
                          </AppTypography>
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: { xs: 1, sm: 1.5 },
                          minWidth: 0,
                        }}
                      >
                        <AppTypography
                          variant="body1"
                          fontWeight={400}
                          fontSize={{ xs: "12px", sm: "14px" }}
                          sx={{ color: "#101828", flexShrink: 0 }}
                        >
                          {String.fromCodePoint(
                            storeInfo?.store?.currency_symbol || 0x20b9,
                          )}
                          {(item.selling_price || 0).toFixed(2)}
                        </AppTypography>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            borderRadius: "20px",
                            border: "1px solid #E5E7EB",
                            backgroundColor: "#F3F4F6",
                            overflow: "hidden",
                            flexShrink: 0,
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleQuantityChange(
                                index,
                                item.selected_quantity,
                                -1,
                              )
                            }
                            disabled={item.selected_quantity <= 1}
                            sx={{
                              padding: { xs: "2px 4px", sm: "4px 8px" },
                              color: "#101828",
                              "&:hover": {
                                backgroundColor: "#E5E7EB",
                                ...theme?.cart_styles?.hoverButton,
                              },
                              "&.Mui-disabled": { color: "#9CA3AF" },
                            }}
                          >
                            <RemoveIcon sx={{ fontSize: "1rem" }} />
                          </IconButton>
                          <AppTypography
                            variant="body2"
                            fontWeight={500}
                            sx={{
                              minWidth: { xs: 20, sm: 28 },
                              textAlign: "center",
                              fontSize: { xs: "12px", sm: "14px" },
                              color: "#101828",
                            }}
                          >
                            {item.selected_quantity}
                          </AppTypography>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleQuantityChange(
                                index,
                                item.selected_quantity,
                                1,
                              )
                            }
                            disabled={
                              item.selected_quantity >= getMaxQuantity(item)
                            }
                            sx={{
                              padding: { xs: "2px 4px", sm: "4px 8px" },
                              color: "#101828",
                              "&:hover": {
                                backgroundColor: "#E5E7EB",
                                ...theme?.cart_styles?.hoverButton,
                              },
                              "&.Mui-disabled": { color: "#9CA3AF" },
                            }}
                          >
                            <AddIcon sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        justifyContent: "space-between",
                        minHeight: { xs: "80px", sm: "112px" },
                        flexShrink: 0,
                      }}
                    >
                      <IconButton
                        onClick={() => handleRemoveItem(index)}
                        size="small"
                        sx={{
                          color: "#6B7280",
                          "&:hover": {
                            color: "#DC2626",
                            backgroundColor: "rgba(220, 38, 38, 0.08)",
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: "1rem" }} />
                      </IconButton>
                      <AppTypography
                        variant="body1"
                        fontWeight={600}
                        fontSize="14px"
                        sx={{ color: "#101828" }}
                      >
                        {String.fromCodePoint(
                          storeInfo?.store?.currency_symbol || 0x20b9,
                        )}
                        {(
                          item.selected_quantity * (item.selling_price || 0)
                        )?.toFixed(2)}
                      </AppTypography>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Grid>

          {/* Order Summary */}
          <Grid item xs={12} lg={4}>
            <Paper
              elevation={0}
              sx={{
                ...theme?.layout_styles?.paper,
                backgroundColor: "#FFFFFF",
                borderRadius: "14px",
                boxShadow:
                  "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px;",
                p: { xs: 2, sm: 3 },
                height: "fit-content",
              }}
            >
              <AppTypography
                variant="h6"
                fontWeight={600}
                sx={{
                  mb: 3,
                  fontSize: "17px",
                  color: "#101828",
                }}
              >
                Order Summary
              </AppTypography>

              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1.5,
                  }}
                >
                  <AppTypography
                    variant="body2"
                    sx={{ fontSize: "14px", fontWeight: 400, color: "#101828" }}
                  >
                    Subtotal ({cart?.length} items)
                  </AppTypography>
                  <AppTypography
                    variant="body2"
                    sx={{ fontSize: "14px", fontWeight: 400, color: "#101828" }}
                  >
                    {String.fromCodePoint(
                      storeInfo?.store?.currency_symbol || 0x20b9,
                    )}
                    {totalPrice?.toFixed(2)}
                  </AppTypography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1.5,
                  }}
                >
                  <AppTypography
                    variant="body2"
                    sx={{ fontSize: "14px", fontWeight: 400, color: "#101828" }}
                  >
                    Shipping
                  </AppTypography>
                  <AppTypography
                    variant="body2"
                    sx={{ fontSize: "14px", fontWeight: 400, color: "#101828" }}
                  >
                    Free
                  </AppTypography>
                </Box>
                <Divider sx={{ my: 2, borderColor: "#99A1AF" }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <AppTypography
                    variant="h6"
                    fontWeight={400}
                    sx={{ fontSize: "16px", color: "#101828" }}
                  >
                    Total
                  </AppTypography>
                  <AppTypography
                    variant="h6"
                    fontWeight={700}
                    sx={{ fontSize: "18px", color: "#101828" }}
                  >
                    {String.fromCodePoint(
                      storeInfo?.store?.currency_symbol || 0x20b9,
                    )}
                    {totalPrice?.toFixed(2)}
                  </AppTypography>
                </Box>
              </Box>

              <AppButton
                variant="contained"
                size="medium"
                fullWidth
                onClick={handleCheckout}
                disabled={cart?.length === 0}
                endIcon={<ChevronRightIcon sx={{ fontSize: "1.1rem" }} />}
                sx={{
                  backgroundColor: theme?.app_button_color || "#6F42C1",
                  color: theme?.app_button_text_color || "#fff",
                  borderRadius: "10px",
                  maxHeight: "40px",
                  minHeight: "40px",
                  py: 0.75,
                  px: 1.5,
                  fontSize: "14px",
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor:
                      theme?.app_button_color || theme?.app_button_color,
                    opacity: 0.95,
                  },
                  "&:disabled": {
                    backgroundColor: "rgba(0, 0, 0, 0.12)",
                    opacity: 0.5,
                  },
                }}
              >
                Proceed to Checkout
              </AppButton>

              <AppButton
                variant="contained"
                size="medium"
                fullWidth
                onClick={handleContinueShopping}
                sx={{
                  mt: 1.5,
                  backgroundColor:
                    theme?.app_button_secondary_hover_color || "#6F42C1",
                  color: theme?.app_button_text_color || "#ffff",
                  borderRadius: "10px",
                  maxHeight: "40px",
                  minHeight: "40px",
                  py: 0.75,
                  px: 1.5,
                  fontSize: "14px",
                  fontWeight: 500,
                  "&:hover": {
                    borderColor:
                      theme?.app_hover_button_color || theme?.app_button_color,
                    color: theme?.app_button_text_color || "#ffff",

                    backgroundColor: theme?.app_button_secondary_hover_color
                      ? `${theme.app_button_secondary_hover_color}`
                      : "rgba(111, 66, 193, 0.04)",
                  },
                }}
              >
                Continue Shopping
              </AppButton>

              {/* <AppTypography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "center",
                  mt: 2,
                  fontSize: "12px",
                  color: "#6B7280",
                }}
              >
                Secure checkout powered by Swageazy
              </AppTypography> */}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default Cart;
